import "./UnsupervisedPage.css";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiRequest, logout } from "../utils/api";

function UnsupervisedLearning() {
    const [columns, setColumns] = useState([]);
    const [selectedColumns, setSelectedColumns] = useState([]);
    const [loading, setLoading] = useState(false);
    const [time, setTime] = useState("");
    const navigate = useNavigate();

    useEffect(() => {
        fetchColumns();
        const interval = setInterval(() => {
            const currentTime = new Date();
            const hours = currentTime.getHours() % 12 || 12;
            const minutes = currentTime.getMinutes().toString().padStart(2, "0");
            const seconds = currentTime.getSeconds().toString().padStart(2, "0");
            const ampm = currentTime.getHours() >= 12 ? "PM" : "AM";
            setTime(`${hours}:${minutes}:${seconds} ${ampm}`);
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    const handleLogout = async () => {
        try {
            await logout();
            navigate('/login');
        } catch (error) {
            console.error('Logout error:', error);
            navigate('/login');
        }
    };

    const fetchColumns = async () => {
        setLoading(true);
        try {
            const data = await apiRequest("/api/dataset/columns");
            if (data.status === "success") {
                setColumns(data.columns);
            } else {
                console.error("Error fetching columns:", data.message);
            }
        } catch (error) {
            console.error("Error fetching columns:", error);
        }
        setLoading(false);
    };

    const handleToggleColumn = (column) => {
        const updatedColumns = selectedColumns.includes(column)
            ? selectedColumns.filter((col) => col !== column)
            : [...selectedColumns, column];
        setSelectedColumns(updatedColumns);
        localStorage.setItem("selectedColumns", JSON.stringify(updatedColumns));
    };

    const handleNextStep = () => {
        if (selectedColumns.length > 0) {
            navigate("/unsupervised-algorithm-selection");
        }
    };

    return (
        <div className="unsupervised-learning">
            {/* Top Navigation Bar */}
            <div className="top-nav">
                <div className="nav-content">
                    <div className="nav-left-group">
                        <div className="nav-logo" onClick={() => navigate("/")} style={{ cursor: "pointer" }}>
                            <img src="/HDASLogo.png" alt="HDAS" style={{ height: "80px", width: "auto" }} />
                        </div>
                        <button className="back-nav-button" onClick={() => navigate("/analysis-selection")}>
                            Analysis Selection
                        </button>
                    </div>
                    <div className="nav-right">
                        <div className="digital-clock">{time}</div>
                        <button onClick={handleLogout} className="logout-button">Logout</button>
                    </div>
                </div>
            </div>

            {/* Header Section */}
            <div className="page-header">
                <div className="header-content">
                    <h1>Unsupervised Learning</h1>
                    <p className="header-subtitle">Select the features you want to use for clustering and pattern discovery</p>
                </div>
            </div>

            {/* Main Content */}
            <div className="page-content">
                <div className="info-box">
                    <h3>Feature Selection Tip</h3>
                    <p>Select multiple features that you believe are relevant for discovering patterns. You can select all features or choose specific ones based on your research question.</p>
                </div>

                {loading ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>Loading columns...</p>
                    </div>
                ) : columns.length === 0 ? (
                    <div className="empty-state">
                        <p>No columns available. Please upload a dataset first.</p>
                        <button onClick={() => navigate("/auto-ml")} className="primary-button">
                            Upload Dataset
                        </button>
                    </div>
                ) : (
                    <>
                        <h2 className="section-title">Select Features ({selectedColumns.length} selected)</h2>
                        <div className="feature-selection">
                            {columns.map((column, index) => (
                                <div
                                    key={index}
                                    className={`feature-box ${selectedColumns.includes(column) ? "selected" : ""}`}
                                    onClick={() => handleToggleColumn(column)}
                                >
                                    <div className="feature-box-icon">
                                        {selectedColumns.includes(column) && <div className="checkmark"></div>}
                                    </div>
                                    <span className="feature-box-text">{column}</span>
                                </div>
                            ))}
                        </div>
                        
                        <div className="action-section">
                            <button 
                                onClick={handleNextStep} 
                                disabled={selectedColumns.length === 0}
                                className={`primary-button ${selectedColumns.length === 0 ? 'disabled' : ''}`}
                            >
                                Continue to Algorithm Selection
                            </button>
                            {selectedColumns.length === 0 && (
                                <p className="help-text">Please select at least one feature to continue</p>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default UnsupervisedLearning;